import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Box, Flex, Text, useColorMode } from '@chakra-ui/react';
import { takeLinkedId } from '../../hooks/useUrlSyncedId';
import { useTranslation } from 'react-i18next';
import { ConvertedAmountsProvider } from '../../contexts/ConvertedAmountsContext';
import { useMovementsData } from '../../hooks/useMovementsData';
import { useTagsView } from '../../hooks/useTagsView';
import { categoryTint } from '../../utils/categoryTint';
import { monthShort } from '../../utils/intlFormatters';
import PhoneSheet from './PhoneSheet';
import { usePhoneShell } from './PhoneShellContext';
import {
  DesktopNoteCard, Disc, ListCard, MovementRows, PhoneState, SectionCaption, ToggleRow, money,
} from './PhoneReadOnlyParts';

const FALLBACK_COLOR = '#6B7B8D';

// A tag whose figures are all zero waits with the inactive ones.
const isShown = (row) => row.is_active !== false && (row.hasSpend || row.hasDebt);

/** "Sep 1 → Sep 30": the period a tag covers, when it has one. */
function periodLabel(tag) {
  const day = (s) => {
    if (!s) return '?';
    const [, m, d] = s.slice(0, 10).split('-').map(Number);
    return `${monthShort(m - 1)} ${d}`;
  };
  if (!tag.start_date && !tag.end_date) return null;
  return `${day(tag.start_date)} → ${day(tag.end_date)}`;
}

/** The currencies a tag has money in, largest first. */
const currenciesOf = (row) => Object.values(row.byCurrency).sort((a, b) => b.spent - a.spent);

function TagDisc({ row, size, fontSize }) {
  const { colorMode } = useColorMode();
  // The tag's own colour as a tint, and the "#" in a shade of it that reads
  // on that tint, whatever colour the user chose.
  const tint = categoryTint(row.color || FALLBACK_COLOR, colorMode);
  return (
    <Disc size={size} fontSize={fontSize} tint={tint.bg}>
      <Box as="span" color={tint.fg} fontWeight="700">#</Box>
    </Disc>
  );
}

function TagRow({ row, onClick }) {
  const { t } = useTranslation('tags');
  const lines = currenciesOf(row);
  const first = lines[0];
  const period = periodLabel(row);
  // A loan envelope with no spending shows what is still owed instead.
  const debt = !row.hasSpend && row.hasDebt
    ? lines.find((c) => c.iOwe > 0 || c.owedToMe > 0)
    : null;
  let amount = '—';
  if (first && first.spent > 0) amount = money(first.currency, first.spent);
  else if (debt) amount = money(debt.currency, debt.iOwe || debt.owedToMe);
  const sub = [
    row.is_active === false && t('row.inactive'),
    debt && (debt.iOwe > 0 ? t('detail.outstanding') : t('detail.owedToMe')),
    period,
    lines.length > 1 && t('phone.moreCurrencies', { count: lines.length - 1 }),
  ].filter(Boolean).join(' · ');

  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      display="grid"
      gridTemplateColumns="40px minmax(0, 1fr) auto"
      columnGap="12px"
      rowGap="2px"
      alignItems="center"
      w="100%"
      minH={sub ? '64px' : '56px'}
      py="9px"
      px="14px"
      textAlign="left"
      opacity={row.is_active === false ? 0.6 : 1}
      _active={{ bg: 'redesign.bg.hover' }}
    >
      <Box gridRow={sub ? '1 / 3' : undefined}><TagDisc row={row} /></Box>
      <Text textStyle="phoneRowName" color="redesign.heading" isTruncated>{row.name}</Text>
      <Text textStyle="phoneRowAmount" color="redesign.heading" textAlign="right" whiteSpace="nowrap">
        {amount}
      </Text>
      {sub && (
        <Text gridColumn="2 / 4" textStyle="phoneMeta" color="phone.muted" isTruncated>{sub}</Text>
      )}
    </Box>
  );
}

/** One figure on its own 44px line: a label on the left, the amount on the right. */
function FigureLine({ label, value, strong = false }) {
  return (
    <Flex align="center" justify="space-between" minH="44px" px="14px" gap="12px">
      <Text textStyle="phoneBody" color={strong ? 'redesign.heading' : 'phone.muted'}>{label}</Text>
      <Text textStyle="phoneRowAmount" fontWeight={strong ? '600' : '500'} color="redesign.heading" whiteSpace="nowrap">
        {value}
      </Text>
    </Flex>
  );
}

/**
 * One tag, read-only: what it adds up to (and, for a loan envelope, what is
 * still owed), how that splits across accounts, and its movements.
 */
function TagSheet({ row, isOpen, onClose }) {
  const { t } = useTranslation('tags');
  const { openMovement, refreshSignal } = usePhoneShell();

  // The same window the desktop panel shows (MI-245): the newest 50 rows,
  // then "Show more".
  const tagId = row?.id;
  const filters = useMemo(
    () => (tagId == null ? null : [{ entity: 'Tag', column: 'id', operator: 'in', values: [tagId] }]),
    [tagId],
  );
  const {
    movementsList, loading, loadingMore, hasMore, loadMore, refetch,
  } = useMovementsData({ filters, enabled: isOpen && tagId != null });

  const lastSignal = useRef(refreshSignal);
  useEffect(() => {
    if (lastSignal.current === refreshSignal) return;
    lastSignal.current = refreshSignal;
    if (isOpen) refetch({ quiet: true });
  }, [refreshSignal, isOpen, refetch]);

  // While another tag's rows load, show none rather than the last tag's.
  const movements = loading ? [] : movementsList;

  const lines = row ? currenciesOf(row) : [];
  const spentLines = lines.filter((c) => c.spent > 0);
  const period = row && periodLabel(row);
  const multi = lines.length > 1;

  return (
    <PhoneSheet id="tag" isOpen={isOpen} onClose={onClose} size="full" title={row?.name}>
      {row && (
        <>
          <Flex direction="column" align="center" pt="14px" px="16px" textAlign="center">
            <TagDisc row={row} size={72} fontSize="34px" />
            <Text fontSize="14px" lineHeight="20px" color="phone.muted" mt="12px">
              {[row.is_active === false && t('row.inactive'), period || t('phone.allTime')].filter(Boolean).join(' · ')}
            </Text>
            {row.description && (
              <Text textStyle="phoneBody" color="redesign.text" mt="8px" textAlign="center">{row.description}</Text>
            )}
            {/* An empty tag says so once, under Movements; a loan with no
                spending leads with its Debt card instead of a zero. */}
            {spentLines.map((c) => (
              <Text key={c.currency} fontSize="28px" lineHeight="34px" fontWeight="700" letterSpacing="-0.02em" color="redesign.heading" mt="2px" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {money(c.currency, c.spent)}
              </Text>
            ))}
            {spentLines.length > 0 && (
              <Text textStyle="phoneMeta" color="phone.muted">{t('columns.spent')}</Text>
            )}
          </Flex>

          {row.hasDebt && (
            <>
              <SectionCaption>{t('phone.debt')}</SectionCaption>
              {lines.filter((c) => c.borrowed > 0 || c.repaid > 0).map((c) => (
                <ListCard key={c.currency} mt="0" dividerInset="14px">
                  {c.borrowed > 0 && <FigureLine label={t('detail.borrowed')} value={money(c.currency, c.borrowed)} />}
                  {c.repaid > 0 && <FigureLine label={t('detail.repaid')} value={money(c.currency, c.repaid)} />}
                  <FigureLine
                    strong
                    label={c.owedToMe > 0 ? t('detail.owedToMe') : t('detail.outstanding')}
                    value={money(c.currency, c.iOwe > 0 ? c.iOwe : c.owedToMe)}
                  />
                </ListCard>
              ))}
            </>
          )}

          {row.hasSpend && (
            <>
              <SectionCaption>{t('detail.spendingPerAccount')}</SectionCaption>
              {lines.filter((c) => c.byAccount.length > 0).map((c) => (
                <Box key={c.currency} mx="16px" mb="8px">
                  {c.byAccount.map((a) => {
                    const pct = c.spent > 0 ? (a.spent / c.spent) * 100 : 0;
                    return (
                      <Box key={a.accountId} py="8px">
                        <Flex align="center" gap="8px" textStyle="phoneMeta">
                          <Box w="8px" h="8px" borderRadius="full" bg={a.color} flexShrink={0} />
                          <Text as="span" flex="1" minW={0} isTruncated textStyle="phoneBody" color="redesign.heading">{a.name}</Text>
                          <Box as="span" color="redesign.text" whiteSpace="nowrap">
                            {multi ? money(c.currency, a.spent) : money(null, a.spent)}
                          </Box>
                          <Box as="span" color="phone.muted" w="40px" textAlign="right">{Math.round(pct)}%</Box>
                        </Flex>
                        <Box h="4px" mt="6px" borderRadius="full" bg="redesign.border">
                          <Box h="100%" w={`${pct}%`} borderRadius="full" bg={a.color} />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </>
          )}

          <DesktopNoteCard />

          <SectionCaption>{t('detail.movements')}</SectionCaption>
          <MovementRows
            movements={movements}
            loading={loading}
            emptyLabel={t('detail.noMovementsYet')}
            onOpen={openMovement}
            onMore={hasMore ? loadMore : null}
            moreLabel={t('movements:list.pagination.loadMore')}
            loadingMore={loadingMore}
          />
        </>
      )}
    </PhoneSheet>
  );
}

/**
 * Tags on the phone, read-only: what each adds up to, largest first, and a
 * detail sheet per tag. Inactive and empty tags wait behind "Show hidden".
 * Tags are created only from the tag picker; editing stays on the computer.
 */
export default function PhoneTags() {
  return (
    <ConvertedAmountsProvider>
      <PhoneTagsList />
    </ConvertedAmountsProvider>
  );
}

function PhoneTagsList() {
  const { t } = useTranslation('tags');
  const { refreshSignal } = usePhoneShell();
  const [showHidden, setShowHidden] = useState(false);
  const [openId, setOpenId] = useState(null);
  const { loading, error, rows, refetch } = useTagsView();

  // `?tag_id=<id>` (a tag in the chat, or a link from the desktop) opens that
  // tag's sheet once the list is in.
  useEffect(() => {
    if (loading) return;
    const linked = takeLinkedId('tag_id');
    if (linked && rows.some((r) => r.id === linked)) setOpenId(linked);
  }, [loading, rows]);

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  useEffect(() => {
    if (refreshSignal) refetchRef.current();
  }, [refreshSignal]);

  const sorted = useMemo(() => [...rows].sort((a, b) => {
    if (isShown(a) !== isShown(b)) return isShown(a) ? -1 : 1;
    return b.grandSpent - a.grandSpent;
  }), [rows]);
  const shown = sorted.filter(isShown);
  const hidden = sorted.filter((r) => !isShown(r));

  const openRow = rows.find((r) => r.id === openId) || null;
  const [lastRow, setLastRow] = useState(null);
  useEffect(() => { if (openRow) setLastRow(openRow); }, [openRow]);

  let list;
  if (loading && rows.length === 0) list = <PhoneState loading />;
  else if (error) list = <PhoneState>{t('phone.loadError')}</PhoneState>;
  else if (rows.length === 0) list = <PhoneState>{t('empty.noTags')}</PhoneState>;
  else {
    list = (
      <>
        {shown.length > 0 && (
          <ListCard mt="12px">
            {shown.map((r) => <TagRow key={r.id} row={r} onClick={() => setOpenId(r.id)} />)}
          </ListCard>
        )}
        {/* With nothing else to show, the inactive and empty tags are the list. */}
        {hidden.length > 0 && shown.length > 0 && (
          <ToggleRow onClick={() => setShowHidden((v) => !v)}>
            {t(showHidden ? 'phone.hideHidden' : 'phone.showHidden', { count: hidden.length })}
          </ToggleRow>
        )}
        {(showHidden || shown.length === 0) && hidden.length > 0 && (
          <ListCard mt={shown.length ? '4px' : '12px'}>
            {hidden.map((r) => <TagRow key={r.id} row={r} onClick={() => setOpenId(r.id)} />)}
          </ListCard>
        )}
      </>
    );
  }

  return (
    <Box pb="8px">
      {list}
      <DesktopNoteCard />
      <TagSheet row={openRow || lastRow} isOpen={Boolean(openRow)} onClose={() => setOpenId(null)} />
    </Box>
  );
}
